import { css } from 'styled-system/css';
import { useState } from 'react';
import {
  accessCtaClass,
  ghostCtaClass,
} from '../../components/landing/AccessCta';
import { Container } from '../../components/landing/Container';
import { MarketingPage } from '../../components/landing/MarketingPage';
import { Reveal } from '../../components/landing/Reveal';
import { appSignupForProTrial, appSignupForStarter } from '../../appOrigin';
import {
  BILLING_FREQUENCIES,
  type BillingFrequency,
} from '../../billing/billingFrequency';
import { paidPro, paidStarter, type PaidTiers } from '../../billing/paidTiers';
import { usePaddle } from '../../billing/usePaddle';
import { usePaddlePrices } from '../../billing/usePaddlePrices';
import { useViewerCountry } from '../../billing/useViewerCountry';
import { AdditionalUsage } from './AdditionalUsage';
import { BillingQuestions } from './BillingQuestions';
import { FrequencyToggle } from './FrequencyToggle';
import { PlanColumn, type PlanCopy } from './PlanColumn';

const business = {
  name: 'Business',
  description: 'Custom volume for growing teams.',
  price: 'Custom',
  priceDetail: 'Priced on volume',
  ctaNote: 'hello@exis.pro',
  includesLabel: 'Everything in Pro, plus',
  points: [
    'Custom monthly volume',
    'SSO and seats, once sharing ships',
    'Excel for the web, later',
  ],
  cta: { href: 'mailto:hello@exis.pro', label: 'Contact us' },
};

const MONTHLY_FALLBACK: Record<PaidTiers['name'], string> = {
  Starter: '$9',
  Pro: '$19',
};

interface ListedPrices {
  prices: Record<string, string>;
  loading: boolean;
  frequency: BillingFrequency;
}

function listedPrice(tier: PaidTiers, listed: ListedPrices): string {
  if (listed.loading) return '…';
  const priceId = tier.priceId[listed.frequency];
  const price = priceId ? listed.prices[priceId] : undefined;
  if (price) return price;
  return listed.frequency === 'month' ? MONTHLY_FALLBACK[tier.name] : '—';
}

export function Pricing() {
  const [frequency, setFrequency] = useState<BillingFrequency>('month');
  const country = useViewerCountry();
  const paddle = usePaddle();
  const { prices, loading } = usePaddlePrices(paddle, country);
  const listed = { prices, loading, frequency };
  const freqMeta = BILLING_FREQUENCIES.find((f) => f.value === frequency)!;
  const proTrialHref = appSignupForProTrial(frequency);
  const starterHref = appSignupForStarter(frequency);
  const starter: PlanCopy = {
    name: paidStarter.name,
    description: paidStarter.description,
    price: listedPrice(paidStarter, listed),
    priceDetail: freqMeta.detail,
    ctaNote: 'No trial, no Exis agents',
    includesLabel: 'Bring your own AI',
    points: paidStarter.features,
  };
  const pro: PlanCopy = {
    name: paidPro.name,
    description: paidPro.description,
    price: listedPrice(paidPro, listed),
    priceDetail: freqMeta.detail,
    ctaNote: '14 day trial, card required',
    includesLabel: 'Everything in Starter, plus',
    points: paidPro.features,
  };

  return (
    <MarketingPage>
      <section
        aria-label="Pricing"
        className={css({ paddingTop: { base: '32', md: '40' } })}
      >
        <Container>
          <Reveal>
            <div
              className={css({
                display: 'flex',
                flexDirection: 'column',
                gap: '5',
                maxWidth: '[720px]',
              })}
            >
              <span
                className={css({
                  textStyle: 'eyebrow',
                  color: 'text.muted',
                })}
              >
                Pricing
              </span>
              <h1
                className={css({
                  textStyle: 'display',
                  fontSize: { base: '4xl', md: '5xl' },
                  letterSpacing: 'tight',
                  color: 'text.primary',
                  textWrap: 'balance',
                  margin: '0',
                })}
              >
                Priced by the cell
              </h1>
              <p
                className={css({
                  fontSize: { base: 'base', md: 'md' },
                  color: 'text.secondary',
                  lineHeight: 'relaxed',
                  textWrap: 'pretty',
                  maxWidth: '[50ch]',
                })}
              >
                Start free by inviting the agent to a Google Sheet. Pick Starter
                when your own AI does the work, or Pro when you want the Exis
                agents working every day. Reading, downloading, and History stay
                open.
              </p>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div
              className={css({
                display: 'grid',
                gridTemplateColumns: {
                  base: '1fr',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                marginTop: { base: '10', md: '14' },
                border: '1px solid token(colors.border.hairline)',
                background: 'bg.surface',
              })}
            >
              <PlanColumn
                plan={starter}
                featured={false}
                cta={
                  <a
                    href={starterHref}
                    data-testid="starter-checkout"
                    className={ghostCtaClass('md')}
                  >
                    Get Starter
                  </a>
                }
              />
              <PlanColumn
                plan={pro}
                featured
                control={
                  <FrequencyToggle
                    frequency={frequency}
                    onSelect={setFrequency}
                  />
                }
                cta={
                  <a
                    href={proTrialHref}
                    data-testid="pro-checkout"
                    className={accessCtaClass('md')}
                  >
                    Start Pro trial
                  </a>
                }
              />
              <PlanColumn
                plan={business}
                featured={false}
                cta={
                  <a href={business.cta.href} className={ghostCtaClass('md')}>
                    {business.cta.label}
                  </a>
                }
              />
              <AdditionalUsage />
            </div>
          </Reveal>
        </Container>
      </section>
      <BillingQuestions />
    </MarketingPage>
  );
}
